import { c as _c } from "react/compiler-runtime";
import type { FetchError } from '@data/api/client/FetchError';
import { getQueryClient } from '@data/queries/queryClient';
import { TPage } from '@data/queries/types';
import { extendQueryResult } from '@data/queries/util/extendQueryResult';
import { flattenPaginatedData, getFetchMoreCursorPagination, getFetchMoreOffsetPagination } from '@data/queries/util/pagination';
import { FetchInfiniteQueryOptions, InfiniteData, QueryKey, UseInfiniteQueryOptions, UseInfiniteQueryResult as UseRqInfiniteQueryResult,
// eslint-disable-next-line no-restricted-imports
useInfiniteQuery as useRqInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * The object that each data hook passes to `useInfiniteQuery`
 */
type Options<TEntry, TQueryKey extends QueryKey, TApiError extends ApiError = FetchError<unknown>> = Omit<UseInfiniteQueryOptions<TPage<TEntry>, TApiError, TPage<TEntry>, TQueryKey, number>, 'initialPageParam' | 'getNextPageParam'>;

/**
 * The other options that a data hook might accept and pass along to `useInfiniteQuery`. It's the same as `Options` but without `queryKey` and `queryFn`.
 */
export type InfiniteQueryOptions<TEntry, TQueryKey extends QueryKey, TApiError extends ApiError = FetchError<unknown>> = Omit<Options<TEntry, TQueryKey, TApiError>, 'queryKey' | 'queryFn'>;
export type InfiniteQueryResult<TEntry, TQueryKey extends QueryKey, TApiError extends ApiError = FetchError<unknown>, Facets extends string = string> = Omit<UseRqInfiniteQueryResult<TPage<TEntry>, TApiError>, 'data'> & {
  data: ReadonlyArray<TEntry>;
  serverTotal: number;
  queryKey: TQueryKey;
  facets?: TFacets<Facets>;
};
function getNextPageParam<TEntry>(lastPage: TPage<TEntry>) {
  if (!lastPage) {
    return undefined;
  }
  if (lastPage.paginationType === 'offset') {
    return getFetchMoreOffsetPagination(lastPage);
  }
  return getFetchMoreCursorPagination(lastPage);
}
export function useInfiniteQuery(options) {
  const $ = _c(10);
  let t0;
  if ($[0] !== options) {
    t0 = {
      initialPageParam: 0,
      ...options,
      getNextPageParam
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const config = t0;
  const rqResult = useRqInfiniteQuery(config);
  const pages = (rqResult.data as InfiniteData<TPage<TEntry>, number> | undefined)?.pages;
  let serverTotal = 0;
  let facets;
  if (pages) {
    const lastPage = pages[pages.length - 1];
    if (lastPage?.paginationType === "offset") {
      serverTotal = lastPage.total;
    }
    if (pages.length > 0 && pages[0]) {
      facets = pages[0].facets;
    }
  }
  let t1;
  if ($[2] !== pages) {
    t1 = flattenPaginatedData(pages);
    $[2] = pages;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const data = t1;
  let t2;
  if ($[4] !== data || $[5] !== facets || $[6] !== options.queryKey || $[7] !== rqResult || $[8] !== serverTotal) {
    t2 = extendQueryResult(rqResult, {
      data,
      queryKey: options.queryKey,
      serverTotal,
      facets
    });
    $[4] = data;
    $[5] = facets;
    $[6] = options.queryKey;
    $[7] = rqResult;
    $[8] = serverTotal;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const result = t2;
  return result;
}
export async function prefetchInfiniteQuery<TEntry, TQueryKey extends QueryKey>(options: Omit<FetchInfiniteQueryOptions<TPage<TEntry>, ApiError, TPage<TEntry>, TQueryKey, number>, 'initialPageParam'>): Promise<void> {
  return await getQueryClient().prefetchInfiniteQuery<TPage<TEntry>, ApiError, TPage<TEntry>, TQueryKey, number>({
    ...options,
    initialPageParam: 0,
    getNextPageParam
  });
}